import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ComicReel from '../components/landing/ComicReel';
import RealToolShowcase from '../components/landing/RealToolShowcase';
import logo from '../assets/Logo_Horizontal_Full.png';
import PortalFilm from '../components/landing/PortalFilm';
import PublicFooter from '../components/landing/PublicFooter';
import { CONTACT_EMAIL, WORKSPACE_TOOL_COUNT } from '../components/landing/landingFacts';
import { usePageMeta } from '../hooks/usePageMeta';
import { getPublicTools } from '../services/publicToolsApi';
import translationIcon from '../assets/tool-brand/translation-management/icon.svg';
import pluginIcon from '../assets/tool-brand/plugin-registration/icon.svg';
import solutionIcon from '../assets/tool-brand/solution-builder/icon.svg';
import deploymentIcon from '../assets/tool-brand/solution-deployment/icon.svg';
import pcfIcon from '../assets/tool-brand/pcf-builder/icon.svg';
import securityIcon from '../assets/tool-brand/security-role-manager/icon.svg';
import driftIcon from '../assets/tool-brand/environment-drift-monitor/icon.svg';
import fetchIcon from '../assets/tool-brand/fetchxml-builder/icon.svg';
import importIcon from '../assets/tool-brand/csv-record-import/icon.svg';
import bulkIcon from '../assets/tool-brand/bulk-data-operations/icon.svg';
import qualityIcon from '../assets/tool-brand/data-quality-assistant/icon.svg';
import formIcon from '../assets/tool-brand/form-designer/icon.svg';
import '../styles/LandingPage.scss';

const Arrow = ({ diagonal = false }: { diagonal?: boolean }) => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {diagonal ? <path d="M6 18 18 6M6 6h12v12" /> : <path d="M4 12h16m-6-6 6 6-6 6" />}
</svg>;
// Same category names as the workspace catalog (locales/it: tools.categories).
const categories = ['Tutti', 'Sviluppo', 'Dati', 'Personalizzazione', 'Amministrazione', 'Governance'] as const;
const tools = [
    { name: 'Plugin Registration', code: 'plugin-registration', category: 'Sviluppo', icon: pluginIcon, description: 'Registra assembly, step e immagini dei plugin direttamente dal browser.' },
    { name: 'PCF Builder', code: 'pcf-builder', category: 'Sviluppo', icon: pcfIcon, description: 'Progetta e sviluppa componenti per dare forma alle tue interfacce.' },
    { name: 'FetchXML Builder', code: 'fetchxml-builder', category: 'Sviluppo', icon: fetchIcon, description: 'Costruisci le query FetchXML e lavora sui risultati nello stesso spazio.' },
    { name: 'CSV & Excel Record Import', code: 'csv-record-import', category: 'Dati', icon: importIcon, description: 'Crea e aggiorna record da file CSV o Excel, con l’anteprima prima di scrivere.' },
    { name: 'Bulk Data Operations', code: 'bulk-data-operations', category: 'Dati', icon: bulkIcon, description: 'Aggiorna, assegna, cambia stato o elimina molti record insieme, con l’esito di ognuno.' },
    { name: 'Data Quality Assistant', code: 'data-quality-assistant', category: 'Dati', icon: qualityIcon, description: 'Trova duplicati, campi vuoti o mai usati e formati non validi, in sola lettura.' },
    { name: 'Translation Studio', code: 'translation-management', category: 'Personalizzazione', icon: translationIcon, description: 'Traduci tabelle, campi, form e viste in tutte le lingue del tuo ambiente.' },
    { name: 'Form Designer', code: 'form-designer', category: 'Personalizzazione', icon: formIcon, description: 'Modifica tab, sezioni, campi e controlli delle form con il drag & drop.' },
    { name: 'Solution Builder', code: 'solution-builder', category: 'Amministrazione', icon: solutionIcon, description: 'Cerca i componenti, aggiungi le dipendenze e componi le tue solution.' },
    { name: 'Solution Deployment Manager', code: 'solution-deployment', category: 'Amministrazione', icon: deploymentIcon, description: 'Porta le solution da Test a UAT a Produzione, con controlli e approvazioni.' },
    { name: 'Security Role Manager', code: 'security-role-manager', category: 'Governance', icon: securityIcon, description: 'Leggi e confronta ruoli e privilegi con una visione più chiara.' },
    { name: 'Environment Drift Monitor', code: 'environment-drift-monitor', category: 'Governance', icon: driftIcon, description: 'Fotografa i metadati e confronta gli ambienti tra loro o nel tempo.' },
];
const questions = [
    { question: 'A chi è pensato Semplora?', answer: 'A sviluppatori, consulenti e amministratori che lavorano con Microsoft Dynamics 365, Dataverse e Power Platform. Gli strumenti coprono attività di sviluppo, gestione dei dati e amministrazione degli ambienti.' },
    { question: 'Devo installare qualcosa?', answer: 'Il portale e molti strumenti si usano direttamente dal browser. Alcune funzionalità, come le integrazioni IA tramite CLI e specifiche operazioni locali, richiedono il Desktop Agent. I requisiti sono indicati prima di utilizzare la funzionalità.' },
    { question: 'Come posso iniziare?', answer: 'Accedi con Microsoft, Google o GitHub e completa il tuo profilo. L’accesso agli strumenti dipende dall’approvazione dell’account e dai permessi assegnati. Una volta abilitato, aggiungi il tuo ambiente e scegli il tool con cui lavorare.' },
    { question: 'Posso usare più ambienti Dynamics 365?', answer: 'Sì. Puoi salvare più connessioni e scegliere l’ambiente con cui lavorare. Ogni strumento mantiene il riferimento alla propria connessione, così hai sempre chiaro il contesto dell’operazione.' },
    { question: 'Come vengono gestiti credenziali e dati?', answer: 'Le connessioni sono salvate nel browser in un vault cifrato; il server utilizza connessioni temporanee per la sessione. Alcuni strumenti producono dati operativi, diagnostica, scansioni o snapshot con regole di conservazione specifiche. L’utilizzo di provider IA dipende dalla configurazione scelta.' },
];
// Comic panel → real tool screen shown next to it (RealToolShowcase index).
const showcaseForPanel: Record<number, number> = { 3: 0, 4: 1 };

export default function LandingPage() {
    const [menuOpen, setMenuOpen] = useState(false);
    const [category, setCategory] = useState<typeof categories[number]>('Tutti');
    const [showcase, setShowcase] = useState(0);
    // Tool con una pagina pubblica: la card porta lì invece che al login.
    const [published, setPublished] = useState<Set<string>>(() => new Set());
    const root = useRef<HTMLDivElement>(null);
    const menuButton = useRef<HTMLButtonElement>(null);
    const visibleTools = tools.filter(tool => category === 'Tutti' || tool.category === category);
    const countLabel = category === 'Tutti'
        ? `${tools.length} dei ${WORKSPACE_TOOL_COUNT} tool del workspace`
        : `${visibleTools.length} tool · ${category}`;

    usePageMeta({
        title: 'Semplora — Il tuo lavoro. Più semplice.',
        description: `I tool per Dynamics 365, Dataverse e Power Platform in un unico workspace: ${WORKSPACE_TOOL_COUNT} strumenti per traduzioni, plugin, solution, dati e ruoli, direttamente nel browser.`,
        path: '/',
    });

    useEffect(() => {
        const elements = root.current?.querySelectorAll<HTMLElement>('[data-sp-reveal]');
        if (!elements || !('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        const observer = new IntersectionObserver(entries => entries.forEach(entry => {
            if (entry.isIntersecting) { entry.target.classList.add('is-visible'); observer.unobserve(entry.target); }
        }), { threshold: 0.08 });
        elements.forEach(element => { element.classList.add('sp-reveal-ready'); observer.observe(element); });
        return () => observer.disconnect();
    }, []);

    // Quali tool hanno già una pagina pubblica: se l'API non risponde le card restano verso il login.
    useEffect(() => {
        const controller = new AbortController();
        getPublicTools(controller.signal)
            .then(list => setPublished(new Set(list.map(tool => tool.code))))
            .catch(() => undefined);
        return () => controller.abort();
    }, []);

    useEffect(() => {
        if (!menuOpen) return;
        const close = (event: KeyboardEvent) => { if (event.key === 'Escape') { setMenuOpen(false); menuButton.current?.focus(); } };
        window.addEventListener('keydown', close);
        return () => window.removeEventListener('keydown', close);
    }, [menuOpen]);

    const followComic = (panel: number) => {
        if (panel in showcaseForPanel) setShowcase(showcaseForPanel[panel]);
    };

    return <div className="sp-public" ref={root}>
        <a className="sp-skip" href="#contenuto">Vai al contenuto</a>
        <header className="sp-nav">
            <Link to="/" className="sp-nav__brand" aria-label="Semplora, homepage">
                <img src={logo} alt="Semplora — Work made simple" width="174" height="58" />
            </Link>
            <nav id="sp-navigation" className={`sp-nav__links${menuOpen ? ' is-open' : ''}`} aria-label="Navigazione principale">
                <a href="#workspace" onClick={() => setMenuOpen(false)}>Il workspace</a>
                <a href="#tools" onClick={() => setMenuOpen(false)}>Gli strumenti</a>
                <a href="#approccio" onClick={() => setMenuOpen(false)}>Il nostro approccio</a>
                <a href="#faq" onClick={() => setMenuOpen(false)}>FAQ</a>
            </nav>
            <div className="sp-nav__actions">
                <Link className="sp-button sp-button--dark sp-nav__login" to="/login">Accedi <Arrow />
                </Link>
                <button ref={menuButton} className="sp-nav__toggle" type="button" aria-label={menuOpen ? 'Chiudi menu' : 'Apri menu'} aria-expanded={menuOpen} aria-controls="sp-navigation" onClick={() => setMenuOpen(!menuOpen)}>
                    <span />
                    <span />
                </button>
            </div>
        </header>
        <main id="contenuto">
            <section className="sp-hero" aria-labelledby="sp-hero-title">
                <div className="sp-hero__grid" aria-hidden="true" />
                <div className="sp-hero__copy">
                    <h1 id="sp-hero-title">Dynamics 365,<br />
                        <span>senza giri a vuoto.</span>
                    </h1>
                    <p className="sp-hero__description">Traduzioni, plugin, solution, dati e ruoli di sicurezza: {WORKSPACE_TOOL_COUNT} tool per Dataverse in un solo portale, direttamente nel browser.</p>
                    <div className="sp-hero__actions">
                        <Link className="sp-button sp-button--mint" to="/login">Entra in Semplora <Arrow />
                        </Link>
                        <a className="sp-hero__explore" href="#workspace">
                            <span className="sp-play" aria-hidden="true">↓</span>Guarda la storia di Luca e Sara</a>
                    </div>
                    <p className="sp-hero__note">Disponibile per Dynamics 365, Dataverse e Power Platform.</p>
                </div>
                <PortalFilm />
                <div className="sp-hero__bottom">
                    <span>FATTO PER CHI COSTRUISCE, OGNI GIORNO.</span>
                    <a href="#workspace">SCOPRI COSA PUOI FARE <span aria-hidden="true">↓</span>
                    </a>
                </div>
            </section>
            <div className="sp-ecosystem" aria-label="Tecnologie con cui lavora Semplora">
                <span>Il tuo ecosistema.<br />
                    <strong>Finalmente, insieme.</strong>
                </span>
                <div>
                    <span className="sp-microsoft" aria-hidden="true">
                        <i />
                        <i />
                        <i />
                        <i />
                    </span>Microsoft Dynamics 365</div>
                <div>
                    <span aria-hidden="true">◇</span>Dataverse</div>
                <div>
                    <span aria-hidden="true">⊞</span>Power Platform</div>
                <div>
                    <span aria-hidden="true">◈</span>Microsoft Entra</div>
            </div>
            <section id="workspace" className="sp-workspace sp-section" aria-labelledby="sp-workspace-title">
                <div className="sp-section-heading" data-sp-reveal>
                    <div>
                        <p className="sp-eyebrow">IL WORKSPACE</p>
                        <h2 id="sp-workspace-title">Una giornata con Semplora.</h2>
                    </div>
                    <p>Segui Luca e Sara: dalla pila di pratiche al lavoro finito, con tre tool reali del workspace.</p>
                </div>
                <div className="sp-workspace__stage" data-sp-reveal>
                    <ComicReel onPanelChange={followComic} />
                    <RealToolShowcase selected={showcase} onSelect={setShowcase} />
                </div>
            </section>
            <section id="tools" className="sp-catalog sp-section" aria-labelledby="sp-tools-title">
                <div className="sp-section-heading" data-sp-reveal>
                    <div>
                        <p className="sp-eyebrow">GLI STRUMENTI</p>
                        <h2 id="sp-tools-title">{WORKSPACE_TOOL_COUNT} tool per Dynamics 365 e Dataverse.</h2>
                    </div>
                    <p>Sviluppo, dati, personalizzazione, amministrazione e governance. Qui trovi una selezione: il catalogo completo è nel workspace.</p>
                </div>
                <div className="sp-catalog__filters" role="group" aria-label="Filtra gli strumenti per categoria">
                    {categories.map(item => <button type="button" key={item} aria-pressed={category === item} onClick={() => setCategory(item)}>
                        {item}
                    </button>)}
                    <span className="sp-catalog__count" role="status">
                        {countLabel}</span>
                </div>
                <div className="sp-catalog__grid">
                    {visibleTools.map(tool => <Link to={published.has(tool.code) ? `/strumenti/${tool.code}` : '/login'} className="sp-tool" key={tool.name}>
                        <div className="sp-tool__top">
                            <img src={tool.icon} alt="" width="58" height="58" loading="lazy" />
                            <span>
                                {tool.category}
                            </span>
                        </div>
                        <h3>
                            {tool.name}
                        </h3>
                        <p>
                            {tool.description}
                        </p>
                        <div className="sp-tool__bottom">
                            <span>{published.has(tool.code) ? 'Scopri il tool' : 'Accedi per usarlo'}</span>
                            <Arrow />
                        </div>
                    </Link>)}
                </div>
                <p className="sp-catalog__note">Gli altri {WORKSPACE_TOOL_COUNT - tools.length} tool, tra cui Metadata Browser, Flow & Logic App Monitor e Gestione Listini, ti aspettano nel workspace.{published.size > 0 && <Link to="/strumenti">Tutte le pagine degli strumenti <Arrow /></Link>}<Link to="/login">Accedi <Arrow />
                </Link>
                </p>
            </section>
            <section className="sp-intelligence" aria-labelledby="sp-ai-title">
                <div className="sp-intelligence__copy" data-sp-reveal>
                    <p className="sp-eyebrow">L’IA NEL WORKSPACE</p>
                    <h2 id="sp-ai-title">L’IA che conosce il tuo ambiente.</h2>
                    <p>Porta l’intelligenza artificiale nel tuo flusso di lavoro. Esplora il tuo ambiente, prepara traduzioni e ragiona sui dati con gli strumenti IA di Semplora.</p>
                    <Link className="sp-button sp-button--mint" to="/login">Scopri l’IA nel workspace <Arrow />
                    </Link>
                    <small>Provider e funzionalità dipendono dalla configurazione.<br />Le integrazioni locali richiedono il Desktop Agent.</small>
                </div>
                <div className="sp-intelligence__visual" data-sp-reveal>
                    <span className="sp-intelligence__star" aria-hidden="true">✳</span>
                    <div className="sp-ai-card">
                        <div className="sp-ai-card__heading">
                            <span aria-hidden="true">✧</span>
                            <strong>Desktop Agent</strong>
                            <span>IL TUO ALLEATO</span>
                        </div>
                        <p>Un nuovo punto di vista<br />sul tuo ambiente.</p>
                        <div className="sp-ai-card__capabilities">
                            <span>Esplorazione dei dati</span>
                            <span>Supporto alle traduzioni</span>
                            <span>Analisi del contesto</span>
                        </div>
                        <div className="sp-ai-card__footer">
                            <i aria-hidden="true" />Tu scegli il prossimo passo.</div>
                    </div>
                    <span className="sp-intelligence__caption">INTELLIGENZA, DENTRO IL TUO FLUSSO.</span>
                </div>
            </section>
            <section id="approccio" className="sp-approach sp-section" aria-labelledby="sp-approach-title">
                <div className="sp-section-heading" data-sp-reveal>
                    <div>
                        <p className="sp-eyebrow">IL NOSTRO APPROCCIO</p>
                        <h2 id="sp-approach-title">La semplicità è una scelta di progetto.</h2>
                    </div>
                    <p>Semplora nasce dal lavoro quotidiano su Dynamics 365. Dalle cose che richiedevano troppi passaggi. E dalla voglia di farle meglio.</p>
                </div>
                <div className="sp-principles">
                    <article data-sp-reveal>
                        <div className="sp-principles__visual sp-principles__visual--browser" aria-hidden="true">
                            <div>
                                <i />
                                <i />
                                <i />
                                <span>semplora</span>
                            </div>
                            <span>↗</span>
                        </div>
                        <h3>Il browser è il tuo spazio.</h3>
                        <p>Apri il portale e ritrova i tuoi strumenti. Un’esperienza condivisa per attività diverse, senza saltare continuamente da un’app all’altra.</p>
                    </article>
                    <article data-sp-reveal>
                        <div className="sp-principles__visual sp-principles__visual--environments" aria-hidden="true">
                            <span>DEV</span>
                            <i />
                            <span>TEST</span>
                            <i />
                            <span>PROD</span>
                        </div>
                        <h3>Il contesto, sempre chiaro.</h3>
                        <p>Più ambienti, connessioni riconoscibili e strumenti organizzati. Mantieni il riferimento a dove stai lavorando, in ogni momento.</p>
                    </article>
                    <article data-sp-reveal>
                        <div className="sp-principles__visual sp-principles__visual--control" aria-hidden="true">
                            <span>✓</span>
                            <div>
                                <i />
                                <i />
                                <i />
                            </div>
                        </div>
                        <h3>Il controllo resta tuo.</h3>
                        <p>Accesso con il tuo provider, permessi del tuo ambiente e passaggi di verifica. Le operazioni importanti meritano scelte consapevoli.</p>
                    </article>
                </div>
            </section>
            <section className="sp-horizon sp-section" aria-labelledby="sp-horizon-title">
                <div className="sp-section-heading" data-sp-reveal>
                    <div>
                        <p className="sp-eyebrow">DOVE STIAMO ANDANDO</p>
                        <h2 id="sp-horizon-title">Oggi Dynamics 365. Domani, altri ecosistemi.</h2>
                    </div>
                    <p>Oggi Semplora lavora con Dynamics 365, Dataverse e Power Platform. Vogliamo portare lo stesso approccio anche su altre piattaforme, a partire da Salesforce.</p>
                </div>
            </section>
            <section id="faq" className="sp-faq sp-section" aria-labelledby="sp-faq-title">
                <div data-sp-reveal>
                    <p className="sp-eyebrow">PRIMA DI PARTIRE</p>
                    <h2 id="sp-faq-title">Domande frequenti.</h2>
                    <p>Hai un’esigenza particolare?</p>
                    <a className="sp-text-link" href={`mailto:${CONTACT_EMAIL}`}>Scrivici a {CONTACT_EMAIL} <Arrow diagonal />
                    </a>
                </div>
                <div className="sp-faq__list">
                    {questions.map(item => <details key={item.question}>
                        <summary>
                            {item.question}
                            <span className="sp-faq__plus" aria-hidden="true">+</span>
                        </summary>
                        <p>
                            {item.answer}
                        </p>
                    </details>)}
                </div>
            </section>
            <section className="sp-finale" aria-labelledby="sp-finale-title">
                <div className="sp-finale__line" aria-hidden="true" />
                <p className="sp-eyebrow">IL PROSSIMO PASSO È TUO.</p>
                <h2 id="sp-finale-title">Fai spazio<br />alle tue <em>idee.</em>
                </h2>
                <Link className="sp-button sp-button--dark" to="/login">Entra in Semplora <Arrow />
                </Link>
                <p>Il tuo lavoro. Più semplice.</p>
                <span className="sp-finale__asterisk" aria-hidden="true">✳</span>
            </section>
        </main>
        <PublicFooter backToTop />
    </div>;
}
